/**
 * TeamsSelect — the HMS-styled dropdown used across the Teams area.
 *
 * A native <select> paints its list with OS chrome (the double-arrow spinner
 * on macOS), which is the one control on this screen that never looked like
 * HMS. This is a listbox: same keyboard contract as a select (Up/Down/Home/
 * End/Enter/Escape, type-ahead), HMS blue chrome, and an options panel we
 * control — so an option can carry a second line (department, "you") that a
 * native select cannot render.
 *
 * Value contract matches the native element it replaces: onChange receives
 * the option's ``value`` directly, and '' means "nothing chosen".
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons';

function TeamsSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  ariaLabel,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const typeAhead = useRef({ buffer: '', at: 0 });

  const selectedIndex = options.findIndex((o) => String(o.value) === String(value));
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  // Click-away and Escape both close it, like every other HMS popup.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) close();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, close]);

  // Keep the highlighted row in view when arrowing through a long member list.
  useEffect(() => {
    if (!open || activeIndex < 0 || !listRef.current) return;
    const node = listRef.current.children[activeIndex];
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const commit = (option) => {
    onChange(option.value);
    close();
  };

  const openWith = (index) => {
    setOpen(true);
    setActiveIndex(index);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    const { key } = event;

    if (!open) {
      if (key === 'Enter' || key === ' ' || key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();
        openWith(selectedIndex >= 0 ? selectedIndex : 0);
      }
      return;
    }

    if (key === 'Escape') { event.preventDefault(); close(); return; }
    if (key === 'Tab') { close(); return; }
    if (key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(options.length - 1, i + 1));
      return;
    }
    if (key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (key === 'Home') { event.preventDefault(); setActiveIndex(0); return; }
    if (key === 'End') { event.preventDefault(); setActiveIndex(options.length - 1); return; }
    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      if (options[activeIndex]) commit(options[activeIndex]);
      return;
    }
    if (key.length === 1) {
      // Type-ahead: "bi" jumps to Bianca, same as a native select.
      const now = Date.now();
      const buffer = now - typeAhead.current.at > 800
        ? key.toLowerCase()
        : typeAhead.current.buffer + key.toLowerCase();
      typeAhead.current = { buffer, at: now };
      const found = options.findIndex(
        (o) => o.label.toLowerCase().startsWith(buffer),
      );
      if (found >= 0) setActiveIndex(found);
    }
  };

  return (
    <div
      className={`teams-select ${open ? 'open' : ''} ${disabled ? 'disabled' : ''} ${className}`}
      ref={rootRef}
    >
      <button
        type="button"
        className="teams-select-trigger"
        onClick={() => (open ? close() : openWith(selectedIndex >= 0 ? selectedIndex : 0))}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={`teams-select-value ${selected ? '' : 'placeholder'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className="teams-select-caret" />
      </button>

      {open && (
        <ul className="teams-select-menu" role="listbox" ref={listRef}>
          {options.map((option, index) => (
            <li
              key={option.value === '' ? '__none__' : option.value}
              role="option"
              aria-selected={String(option.value) === String(value)}
              className={`teams-select-option ${index === activeIndex ? 'active' : ''} ${
                String(option.value) === String(value) ? 'selected' : ''
              }`}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => commit(option)}
            >
              <span className="teams-select-option-label">{option.label}</span>
              {option.hint && (
                <span className="teams-select-option-hint">{option.hint}</span>
              )}
              {String(option.value) === String(value) && (
                <FontAwesomeIcon icon={faCheck} className="teams-select-tick" />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default TeamsSelect;
